
import { PageContainer } from "@/components/ui/page-container";
import { MedicationList } from "@/components/medications/MedicationList";
import { MedicationHeader } from "@/components/medications/MedicationHeader";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MedicationConfirmation } from "@/components/medications/MedicationConfirmation";
import { MedicationAdherence } from "@/components/medications/MedicationAdherence";
import { useNotificationsManager } from "@/hooks/use-notifications-manager";

const Medications = () => {
  // Enable real-time notifications for all notifications in one hook
  useNotificationsManager();

  const { data: medications, isLoading } = useQuery({
    queryKey: ["medications"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data, error } = await supabase
        .from("medications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
        
      if (error) throw error;
      return data || [];
    },
  });

  // Get most recent medication for the confirmation card
  const latestMedication = medications && medications.length > 0 
    ? medications[0] 
    : null;

  // Calculate adherence stats from medication data
  const calculateAdherenceStats = () => {
    if (!medications || medications.length === 0) return {
      takenPercentage: 0,
      onTimePercentage: 0,
      correctDosagePercentage: 0
    };

    // For now, we're using sample data, but in a real implementation 
    // this would be calculated based on actual medication logs
    return {
      takenPercentage: 66.7,
      onTimePercentage: 33.3,
      correctDosagePercentage: 66.7
    };
  };

  const adherenceStats = calculateAdherenceStats();

  // Function to determine if a medication is expired
  const getExpiryStatus = (expiryDate: string | null): string | null => {
    if (!expiryDate) return null;
    
    const expiry = new Date(expiryDate);
    const today = new Date();
    
    // Calculate days difference
    const timeDiff = expiry.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    if (daysDiff < 0) return "EXPIRED";
    if (daysDiff <= 30) return "EXPIRING_SOON";
    return "VALID";
  };

  // Function to get color based on expiry status
  const getStatusColor = (status: string | null): string => {
    switch (status) {
      case "EXPIRED":
        return "bg-red-100 text-red-800";
      case "EXPIRING_SOON":
        return "bg-yellow-100 text-yellow-800";
      case "VALID":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <PageContainer 
      title="Médicaments"
      variant="default"
      className="py-6"
    >
      <div className="space-y-6">
        <MedicationHeader />
        
        <div className="grid gap-6 md:grid-cols-2">
          <MedicationConfirmation medication={latestMedication} />
          <MedicationAdherence stats={adherenceStats} />
        </div>
        
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
        ) : (
          <MedicationList 
            medications={medications || []} 
            getExpiryStatus={getExpiryStatus}
            getStatusColor={getStatusColor}
          />
        )}
      </div>
    </PageContainer>
  );
};

export default Medications;
